import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ChangeDetectorRef } from '@angular/core';
import {
  SchemaField,
  Document,
  DocumentSchema,
  SystemFieldType,
  DocumentProximityStatus
} from 'src/app/modules/core/store/models/document.model';
import { DocumentReader } from 'src/app/modules/core/store/models/document-reader';
import { DynamicField } from '../dynamic-field';
import { ViewComponent } from '../../../store/models';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { TranslateService } from '@ngx-translate/core';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { OrgBusinessUnit, OrgBusinessUnitsApiService } from '@app/modules/core/org-business-units';
import equal from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { UserProfileService } from '@app/modules/core/services/user-profile.service';

@Component({
    selector: 'app-business-units-field',
    templateUrl: './business-units-field.component.html',
    styleUrls: ['./business-units-field.component.scss'],
    standalone: false
})
export class BusinessUnitsFieldComponent implements OnInit, DynamicField, OnDestroy {
  @Output() valueChanged = new EventEmitter<DynamicField>();
  @Input() field: SchemaField;
  @Input() displayName: string;
  @Input() layerUId: string;
  @Input() set document(value: Document) {
    this.reader = new DocumentReader(value);
  }
  reader: DocumentReader;
  @Input() viewComponent: ViewComponent;
  isReadOnly: boolean;
  @Input() schema: DocumentSchema;
  @Input() dataset: Dataset;

  businessUnits: OrgBusinessUnit[] = [];
  hasBusinessUnits: boolean;
  hasData: boolean;
  rootUnitUId: string;
  selectedUIds = [];

  get value(): any {
    return this._value ?? null;
  }

  set value(val: any) {
    this._value = val;
    this.selectedUIds = val;
  }

  get displayValue(): any {
    return this.value;
  }

  private _value: any = [];

  showFieldAsEdit: boolean = false;
  private _isEdit: boolean = false;
  @Input() set isEdit(edit: boolean) {
    this._isEdit = edit;

    this.calculateShouldDisplayFieldForEdit();
  }

  calculateShouldDisplayFieldForEdit() {
    this.showFieldAsEdit = this._isEdit && !this.isReadOnly;
  }

  constructor(
    private store: Store<fromUserProfile.State>,
    private translateService: TranslateService,
    private orgBusinessUnitsApiService: OrgBusinessUnitsApiService,
    private userProfileService: UserProfileService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadBusinessUnits();
  }

  private loadBusinessUnits(): void {
    this.hasBusinessUnits = this.userProfileService.getUserHasBusinessUnits();
    if (!this.hasBusinessUnits) {
      this.businessUnits = null;
      this.changeDetector.markForCheck();
      return;
    }
    let businessUnits = this.userProfileService.getUserBusinessUnits();
    // get BU that were selected but we don't have access to
    const noAccessIds = [];
    this.value?.forEach(id => {
      if (!businessUnits.some(u => u.uId === id)) {
        noAccessIds.push(id);
      }
    });
    if (noAccessIds?.length > 0) {
      this.orgBusinessUnitsApiService.getOrgBusinessUnitsByUIds(noAccessIds).subscribe(units => {
        units.forEach(u => {
          u.userHasNoAccess = true;
        });
        this.businessUnits = [...businessUnits, ...units];
        this.processBusinessUnits(this.businessUnits);
      });
    } else {
      this.businessUnits = businessUnits;
      this.processBusinessUnits(this.businessUnits);
    }
  }

  private processBusinessUnits(businessUnits: OrgBusinessUnit[]): void {
    const rootUnit = cloneDeep(businessUnits?.find(u => !!!u.parentUId));
    this.rootUnitUId = null;
    this.selectedUIds = [];

    if (!!rootUnit) {
      rootUnit.name = this.translateService.instant('MANAGEMENT.MENU.GLOBAL-UNIT', { name: rootUnit.name });
      this.rootUnitUId = rootUnit.uId;
    }
    if (!!this.value) {
      if (this.value.length > 0) {
        const value = cloneDeep(this.value);
        this.selectedUIds = value.sort((c1, c2) =>
          businessUnits.find(b => b.uId == c1)?.level > businessUnits.find(b => b.uId == c2)?.level ? 1 : -1
        );
      }
    }

    this.businessUnits = businessUnits;
    this.hasData = true;
    this.changeDetector.markForCheck();
  }

  onBusinessUnitsSelectionChanged(selectedUIds: string[]): void {
    this.value = selectedUIds;
    this.onValueChanged();
  }

  ngOnDestroy(): void {
    this.valueChanged.complete();
  }

  onValueChanged(): void {
    this.valueChanged.emit(this);
  }

  tpOnContentUpdated(): void {
    this.value = this.reader.getFieldValue(this.field?.systemName);

    if ((this.field.fieldType & SystemFieldType.Editable) !== SystemFieldType.Editable || this.viewComponent?.configuration?.isReadOnly) {
      this.isReadOnly = true;
      this.calculateShouldDisplayFieldForEdit();
    }
  }
}
